import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ContactForm from '@/components/feature/ContactForm';
import { siteInfo, testimonials, categories, comics } from '@/mocks/comics';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于073漫画网',
  description: siteInfo.description,
  url: 'http://aduvet.com/about',
  about: {
    '@type': 'Organization',
    name: '073漫画网',
    description: siteInfo.description,
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteInfo.contact.email,
      contactType: 'customer service',
    },
  },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '073漫画网的漫画真的全部免费吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，073漫画网上所有韩国漫画均为免费阅读，无需充值或订阅，打开即可畅享高清无删减韩漫。',
      },
    },
    {
      '@type': 'Question',
      name: '什么是下拉式阅读模式？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '下拉式阅读是一种流畅的垂直滚动阅读方式，无需翻页，只需向下滚动即可连续阅读漫画内容，带来沉浸式的阅读体验。',
      },
    },
    {
      '@type': 'Question',
      name: '073漫画网的更新频率如何？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们每日更新200+话内容，第一时间同步韩国最新的热门漫画，让漫迷们不会错过任何精彩章节。',
      },
    },
    {
      '@type': 'Question',
      name: '是否需要注册才能阅读？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '不需要。073漫画网完全开放，无需注册即可直接在线阅读所有韩国漫画，方便快捷。',
      },
    },
    {
      '@type': 'Question',
      name: '073漫画网收录了哪些类型的漫画？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们涵盖恋爱漫、热血漫、奇幻漫、剧情漫、搞笑漫、悬疑漫、BL漫、GL漫、日常漫、恐怖漫等多种类型，满足不同读者的喜好。',
      },
    },
  ],
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'http://aduvet.com/' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://aduvet.com/about' },
  ],
};

export default function AboutPage() {
  const stats = [
    { value: siteInfo.stats.totalComics, label: '漫画资源', desc: '涵盖恋爱、热血、奇幻等10+类型' },
    { value: siteInfo.stats.dailyUpdate, label: '每日更新', desc: '同步韩国最新热门漫画' },
    { value: siteInfo.stats.totalUsers, label: '漫迷信赖', desc: '千万读者的共同选择' },
    { value: siteInfo.stats.avgRating, label: '用户评分', desc: '高口碑韩漫阅读平台' },
  ];

  return (
    <>
      <SEO
        title="关于我们 - 073漫画网"
        description="了解073漫画网 - 国内领先的韩国漫画在线阅读平台。海量无删减韩漫资源，每日更新，下拉式阅读，高清全彩，完全免费。查看品牌介绍、用户口碑和联系方式。"
        keywords="073漫画网,关于我们,韩国漫画平台,免费韩漫,品牌介绍"
        canonicalUrl="http://aduvet.com/about"
        jsonLd={{ '@context': 'https://schema.org', '@graph': [aboutJsonLd, faqJsonLd, breadcrumbJsonLd] }}
      />

      <Navbar />

      <main className="pt-16">
        <nav className="bg-background-50 border-b border-background-200" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-800 font-medium">关于我们</li>
            </ol>
          </div>
        </nav>

        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl font-extrabold text-foreground-950 font-heading">
              关于073漫画网
            </h1>
            <p className="mt-5 text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              {siteInfo.description}
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-gradient-to-b from-primary-900 to-primary-950 text-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading">我们的数字</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-4xl md:text-5xl font-extrabold font-label text-accent-400">
                    {stat.value}
                  </div>
                  <div className="text-background-50 font-semibold text-lg mt-2">{stat.label}</div>
                  <div className="text-primary-300 text-sm mt-1">{stat.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">用户口碑</h2>
              <p className="text-foreground-500 text-sm mt-2">听听漫友们怎么说</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 rounded-xl p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(t.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed mb-4">{t.comment}</p>
                  <div className="flex items-center gap-3">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                    <div>
                      <p className="text-foreground-900 text-sm font-semibold">{t.name}</p>
                      <p className="text-foreground-500 text-xs">{t.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">漫画类型</h2>
              <p className="text-foreground-500 text-sm mt-2">10+ 类型，总有一款适合你</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {categories.map((cat) => {
                const count = comics.filter((c) => c.categories.includes(cat.slug)).length;
                return (
                  <Link
                    key={cat.slug}
                    to="/category/all"
                    className="flex flex-col items-center gap-2 p-5 bg-background-100 rounded-xl hover:bg-primary-100 transition-colors cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <span className="text-foreground-700 font-medium text-sm">{cat.name}</span>
                    <span className="text-foreground-400 text-xs">{count}部</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">常见问题</h2>
            </div>
            <div className="space-y-3">
              {[
                { q: '073漫画网的漫画真的全部免费吗？', a: '是的，073漫画网上所有韩国漫画均为免费阅读，无需充值或订阅，打开即可畅享高清无删减韩漫。' },
                { q: '什么是下拉式阅读模式？', a: '下拉式阅读是一种流畅的垂直滚动阅读方式，无需翻页，只需向下滚动即可连续阅读漫画内容，带来沉浸式的阅读体验。' },
                { q: '073漫画网的更新频率如何？', a: '我们每日更新200+话内容，第一时间同步韩国最新的热门漫画，让漫迷们不会错过任何精彩章节。' },
                { q: '是否需要注册才能阅读？', a: '不需要。073漫画网完全开放，无需注册即可直接在线阅读所有韩国漫画，方便快捷。' },
                { q: '073漫画网收录了哪些类型的漫画？', a: '我们涵盖恋爱漫、热血漫、奇幻漫、剧情漫、搞笑漫、悬疑漫、BL漫、GL漫、日常漫、恐怖漫等多种类型，满足不同读者的喜好。' },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-50 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors list-none">
                    <span className="text-foreground-800 font-medium text-sm pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">联系我们</h2>
              <p className="text-foreground-500 text-sm mt-2">有任何问题或建议，欢迎随时联系我们</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground-900 text-sm">邮箱</h4>
                    <p className="text-foreground-600 text-sm">{siteInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground-900 text-sm">微信</h4>
                    <p className="text-foreground-600 text-sm">{siteInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground-900 text-sm">地址</h4>
                    <p className="text-foreground-600 text-sm">{siteInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-qq-line"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground-900 text-sm">QQ</h4>
                    <p className="text-foreground-600 text-sm">{siteInfo.contact.qq}</p>
                  </div>
                </div>
              </div>

              <ContactForm />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}